import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { WalletBalance, LedgerPage, ActiveSession, SessionPage, StartSessionSuccess, UsageDaily } from '@lyra/shared';
import { apiFetch, ApiClientError } from './apiClient';
import { queryKeys } from './queryKeys';
import { useMe } from './useMe';
import { canSeeBilling } from './permissions';
import { notifyError, notifySuccess } from './useMutationToast';

// The pure-billing endpoints are 403 for regular members (server billing gate).
// Gate the queries on the same predicate so a member never fires them — no 403,
// and no money in the client cache. `useActiveSession` stays ungated: everyone
// needs the session lifecycle (the drawer), and the server strips its money.
export function useWallet() {
  const { data: me } = useMe();
  return useQuery<WalletBalance>({
    queryKey: queryKeys.wallet,
    queryFn: () => apiFetch<WalletBalance>('/wallet'),
    enabled: canSeeBilling(me),
  });
}

export function useWalletLedger(limit = 50, offset = 0) {
  const { data: me } = useMe();
  return useQuery<LedgerPage>({
    queryKey: queryKeys.ledger(limit, offset),
    queryFn: () => apiFetch<LedgerPage>(`/wallet/ledger?limit=${limit}&offset=${offset}`),
    enabled: canSeeBilling(me),
  });
}

/** `ActiveSession.provisioningState` (Task 8) is Fleet's readiness signal:
 *  `'provisioning'` while the instance is still coming up, `'ready'` once
 *  usable, `null` when Fleet is off (or on a pre-Fleet row) — treated
 *  identically to `'ready'` everywhere it's read (`instanceCardState`,
 *  `shouldShowProvisioningBanner` in `./instanceState.ts`), which is what
 *  keeps Fleet-off behaviour byte-identical to before this field existed. */
export function useActiveSession() {
  return useQuery<ActiveSession | null>({
    queryKey: queryKeys.activeSession,
    queryFn: () => apiFetch<ActiveSession | null>('/instance-sessions/active'),
    // Poll fast while provisioning (so the install-progress bar advances
    // smoothly), slow once active (cost only steps every 6 min), off when idle.
    refetchInterval: (q) => {
      const d = q.state.data;
      if (!d) return false;
      return d.provisioningState === 'provisioning' ? 6_000 : 60_000;
    },
  });
}

export function useSessions(limit = 50, offset = 0) {
  const { data: me } = useMe();
  return useQuery<SessionPage>({
    queryKey: queryKeys.sessions(limit, offset),
    queryFn: () => apiFetch<SessionPage>(`/instance-sessions?limit=${limit}&offset=${offset}`),
    enabled: canSeeBilling(me),
  });
}

export function useUsageDaily() {
  const { data: me } = useMe();
  return useQuery<UsageDaily>({
    queryKey: queryKeys.usageDaily,
    queryFn: () => apiFetch<UsageDaily>('/wallet/usage-daily'),
    enabled: canSeeBilling(me),
  });
}

function useInvalidateWallet() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: queryKeys.activeSession });
    qc.invalidateQueries({ queryKey: queryKeys.wallet });
    qc.invalidateQueries({ queryKey: ['wallet', 'ledger'] });
    qc.invalidateQueries({ queryKey: ['instance-sessions', 'list'] });
    qc.invalidateQueries({ queryKey: queryKeys.usageDaily });
  };
}

/** Start returns `StartSessionSuccess` on 2xx (`StartSessionStarted` for 201
 *  Fleet-off/legacy-immediate, `StartSessionProvisioning` for 202 Fleet-on —
 *  see `shared/src/wallet.ts`'s doc comment for the full per-status
 *  contract); the caller handles thrown `ApiClientError`s for every non-2xx
 *  status (409 conflict, 403 refused, 402 no_credit, 503 no_capacity, 500
 *  config_error/provider_error) — `startSessionErrorMessage` in
 *  `./instanceState.ts` maps those to the inline message to display.
 *  Invalidation runs on `onSettled` (success AND error) rather than
 *  `onSuccess` alone: a 409 (a colleague started/stopped a session
 *  concurrently) still needs a refetch so the UI corrects itself instead of
 *  showing stale state. */
export function useStartSession() {
  const invalidate = useInvalidateWallet();
  return useMutation({
    mutationFn: () => apiFetch<StartSessionSuccess>('/instance-sessions/start', { method: 'POST' }),
    onSettled: invalidate,
    // 202 (Fleet on) returns `StartSessionProvisioning` — the rental was
    // accepted but the instance isn't `ready` yet (see the type's doc
    // comment above); `SessionDrawer` is already announcing "Se pornește
    // instanța de procesare." for that same state via its own live region,
    // so claiming "a pornit." here would contradict it under sonner's
    // aria-live="polite" wrapping. Only 201 (Fleet off / legacy immediate
    // start, `StartSessionStarted`, no `provisioningState` field) means the
    // instance is actually up.
    onSuccess: (data) => notifySuccess(
      'provisioningState' in data ? 'Instanța pornește — durează 2–5 minute.' : 'Instanța a pornit.'
    ),
    // 409 (a colleague started/stopped concurrently) is a benign race, not
    // a failure — `Instanta.tsx`/`instanceState.ts`'s `startSessionErrorMessage`
    // already treats it as "nothing to show" for the same reason (the
    // `onSettled` refetch above self-corrects the UI). Toasting it would
    // read as an error for something that isn't one.
    onError: (error) => {
      if (error instanceof ApiClientError && error.status === 409) return;
      notifyError(error, 'Instanța nu a putut porni.');
    },
  });
}

export function useStopSession() {
  const invalidate = useInvalidateWallet();
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/instance-sessions/${id}/stop`, { method: 'POST' }),
    onSettled: invalidate,
    onSuccess: () => notifySuccess('Instanța a fost oprită.'),
    onError: (error) => notifyError(error, 'Oprirea instanței a eșuat.'),
  });
}
